import { useEffect, useState, useRef } from "react";
import { useLocation } from "wouter";
import { supabase } from "@/lib/supabase";
import { Bot, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AuthCallbackPage() {
  const [, setLocation] = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState("Connexion en cours…");
  // Prevents double execution on re-mount (mobile back/forward, StrictMode, etc.)
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const handleCallback = async () => {
      // Step 1: Check for an existing session FIRST.
      // If the component re-mounts after the code was already exchanged (mobile
      // backgrounding, React StrictMode double-invoke, browser back/forward),
      // the code is gone from the URL but the session is valid. Redirect silently.
      try {
        const { data: { session: existing } } = await supabase.auth.getSession();
        if (existing) {
          setLocation("/");
          return;
        }
      } catch {
        // getSession failure is non-fatal — continue to try the code exchange
      }

      // Step 2: Read URL parameters
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const errorParam = params.get("error");
      const errorDescription = params.get("error_description");

      if (errorParam) {
        setError(errorDescription || errorParam);
        return;
      }

      // Step 3: No session AND no code means a genuine error (not a re-mount)
      if (!code) {
        setError("Code d'autorisation manquant. Veuillez réessayer.");
        return;
      }

      // Step 4: Exchange the code for a session (Supabase native)
      const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
      if (exchangeError || !data.session) {
        setError(exchangeError?.message || "Échec de la connexion Google. Veuillez réessayer.");
        return;
      }

      const email = (data.session.user.email || "").toLowerCase().trim();

      // Step 5: Domain restriction — @jobotto.fr only
      if (!email.endsWith("@jobotto.fr")) {
        await supabase.auth.signOut();
        setLocation("/login?error=domain");
        return;
      }

      // Step 6: Whitelist check
      setStatus("Vérification de votre accès…");
      try {
        const res = await fetch(`/api/auth/check-allowed?email=${encodeURIComponent(email)}`);
        const json = await res.json();
        if (!json.allowed) {
          await supabase.auth.signOut();
          setLocation("/login?error=not_allowed");
          return;
        }
      } catch {
        await supabase.auth.signOut();
        setLocation("/login?error=check_failed");
        return;
      }

      // Step 7: All checks passed — redirect via the app router (no window.location.href)
      setStatus("Accès accordé, redirection…");
      setLocation("/");
    };

    handleCallback();
  }, []); // intentionally empty — must run only once per mount

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30 p-4">
        <div className="w-full max-w-md text-center space-y-6">
          <div className="w-16 h-16 bg-destructive/10 rounded-2xl flex items-center justify-center mx-auto">
            <XCircle className="w-8 h-8 text-destructive" />
          </div>
          <div className="space-y-2">
            <h1 className="text-xl font-semibold text-foreground">Erreur de connexion</h1>
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
          <Button onClick={() => setLocation("/login")} className="gap-2">
            Retour à la connexion
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/30 p-4">
      <div className="text-center space-y-4">
        <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center shadow-lg shadow-primary/20 mx-auto">
          <Bot className="w-8 h-8 text-primary-foreground" />
        </div>
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">{status}</span>
        </div>
      </div>
    </div>
  );
}
